// One resizable, collapsible sidebar for every Dugzi app (D-161). An app calls `useSidebar` once, puts
// `{...sb.hostProps}` on the grid that holds its sidebar (column width `var(--dz-side-w)`), and renders
// `<SidebarHandle sb={sb} />` as a child of that grid (it positions itself on the sidebar's edge). `ResizableSidebar` does all of it for a new sidebar. Width and
// collapsed state are remembered per app and sidebar. At 760 px and narrower nothing here acts: the app's own
// drawer shows its sidebar, so there is no resize handle on a phone.
import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { Tooltip } from './tooltip';
import { NARROW_QUERY, parseStored, resolveWidth, storageKey, clampWidth, type SidebarLimits, type SidebarState } from './sidebar-core';
import { useT } from '../i18n';

export interface UseSidebarOptions extends Partial<SidebarLimits> {
  /** Media query under which the app's own drawer takes over (default: 760 px and narrower). */
  narrowQuery?: string;
  /** `end`: a panel on the inline-end side (comments, format): resizable, never collapsed by dragging; width in `--dz-panel-w`. */
  side?: 'start' | 'end';
}
export interface SidebarApi {
  app: string; id: string;
  width: number; min: number; max: number; collapsed: boolean;
  /** True below 768 px: the app's own drawer is in charge. */
  narrow: boolean;
  side: 'start' | 'end';
  setWidth(w: number): void;
  toggle(): void;
  setCollapsed(c: boolean): void;
  /** Put on the grid (or flex) element that holds the sidebar. */
  hostStyle: CSSProperties;
  hostProps: { 'data-sidebar-host': true; 'data-sidebar-collapsed': boolean | undefined; style: CSSProperties };
}

function read(app: string, id: string, l: SidebarLimits): SidebarState {
  try { return parseStored(localStorage.getItem(storageKey(app, id)), l); } catch { return { width: l.defaultWidth, collapsed: false }; }
}

export function useSidebar(app: string, id: string, o: UseSidebarOptions = {}): SidebarApi {
  const limits: SidebarLimits = { defaultWidth: o.defaultWidth ?? 248, min: o.min ?? 180, max: o.max ?? 420 };
  const [st, setSt] = useState<SidebarState>(() => read(app, id, limits));
  const [narrow, setNarrow] = useState(() => typeof matchMedia === 'function' && matchMedia(o.narrowQuery ?? NARROW_QUERY).matches);
  const lim = useRef(limits); lim.current = limits;
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const mq = matchMedia(o.narrowQuery ?? NARROW_QUERY);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [o.narrowQuery]);
  const save = useCallback((next: SidebarState) => {
    setSt(next);
    try { localStorage.setItem(storageKey(app, id), JSON.stringify(next)); } catch { /* private window: it just isn't remembered */ }
  }, [app, id]);
  const setWidth = useCallback((w: number) => save(o.side === 'end' ? { width: clampWidth(w, lim.current), collapsed: false } : resolveWidth(w, lim.current)), [save, o.side]);
  const setCollapsed = useCallback((c: boolean) => setSt((cur) => { const n = { ...cur, collapsed: c }; try { localStorage.setItem(storageKey(app, id), JSON.stringify(n)); } catch { /* */ } return n; }), [app, id]);
  const toggle = useCallback(() => setCollapsed(!st.collapsed), [setCollapsed, st.collapsed]);
  const side = o.side ?? 'start';
  const active = !narrow;
  const hostStyle = (active ? { [side === 'end' ? '--dz-panel-w' : '--dz-side-w']: st.collapsed ? '0px' : `${clampWidth(st.width, limits)}px` } : {}) as CSSProperties;
  return {
    app, id, width: clampWidth(st.width, limits), min: limits.min, max: limits.max, collapsed: active && st.collapsed, narrow, side, setWidth, toggle, setCollapsed, hostStyle,
    hostProps: { 'data-sidebar-host': true, 'data-sidebar-collapsed': active && st.collapsed ? true : undefined, style: hostStyle },
  };
}

/** The collapse / expand button for an app's header or toolbar. Hidden on phones (the drawer button does it). */
export function SidebarToggle({ sb, label: named, className = '' }: { sb: SidebarApi; label?: string; className?: string }) {
  const t = useT();
  const label = named ?? t('ui.sidebar.name');
  if (sb.narrow) return null;
  const text = `${sb.collapsed ? 'Show' : 'Hide'} ${label}`;
  return (
    <Tooltip content={text}>
      <button type="button" className={`dz-icon-btn dz-sidebar__toggle ${className}`} aria-label={text} aria-expanded={!sb.collapsed} onClick={sb.toggle} data-testid={`sidebar-toggle-${sb.id}`}>
        {sb.collapsed ? <PanelLeftOpen aria-hidden /> : <PanelLeftClose aria-hidden />}
      </button>
    </Tooltip>
  );
}

/** What the handle's native listeners call; `el` is the handle, which holds the pointer capture. */
interface HandleHandlers {
  key(e: KeyboardEvent): void;
  down(e: PointerEvent, el: HTMLElement): void;
  move(e: PointerEvent): void;
  up(e: PointerEvent, el: HTMLElement): void;
  dbl(): void;
}
const NO_HANDLERS: HandleHandlers = { key() {}, down() {}, move() {}, up() {}, dbl() {} };

/**
 * The drag handle on the sidebar's inline-end edge: a focusable separator. Pointer drag resizes (under
 * half the minimum it collapses); arrow keys move 16 px (Shift 64), Home and End jump to the limits,
 * Enter or a double click collapses. While collapsed it shows a small tab to open it again.
 */
export function SidebarHandle({ sb, label: named, inline }: { sb: SidebarApi; label?: string; inline?: boolean }) {
  const t = useT();
  const label = named ?? t('ui.sidebar.resize');
  const drag = useRef<{ x: number; w: number } | null>(null);
  // The splitter's keys and pointer (the WAI-ARIA window splitter) are listeners on the handle, not JSX
  // handlers: jsx-a11y 6.10 matches its per-element options by tag, so it cannot tell this focusable separator
  // from a static <div>. The handle has no children, so the listeners see exactly the events the JSX props saw.
  // They read the latest handlers through `on`, so a re-render never leaves a stale width or side behind.
  const handle = useRef<HTMLDivElement>(null);
  const on = useRef<HandleHandlers>(NO_HANDLERS);
  useEffect(() => {
    const el = handle.current; if (!el) return;
    const ls: [string, (e: Event) => void][] = [
      ['keydown', (e) => on.current.key(e as KeyboardEvent)],
      ['pointerdown', (e) => on.current.down(e as PointerEvent, el)],
      ['pointermove', (e) => on.current.move(e as PointerEvent)],
      ['pointerup', (e) => on.current.up(e as PointerEvent, el)],
      ['pointercancel', (e) => on.current.up(e as PointerEvent, el)],
      ['dblclick', () => on.current.dbl()],
    ];
    for (const [type, fn] of ls) el.addEventListener(type, fn);
    return () => { for (const [type, fn] of ls) el.removeEventListener(type, fn); };
  });
  if (sb.narrow) return null;
  const rtl = typeof document !== 'undefined' && getComputedStyle(document.documentElement).direction === 'rtl';
  const end = sb.side === 'end';
  if (sb.collapsed && !end) {
    return (
      <Tooltip content={t('ui.sidebar.show')}>
        <button type="button" className="dz-sidebar__reopen" aria-label={t('ui.sidebar.show')} onClick={sb.toggle} data-testid={`sidebar-open-${sb.id}`}>
          <PanelLeftOpen aria-hidden size={16} />
        </button>
      </Tooltip>
    );
  }
  on.current = {
    down(e, el) {
      if (e.button !== 0) return;
      drag.current = { x: e.clientX, w: sb.width };
      el.setPointerCapture(e.pointerId);
      document.documentElement.classList.add('dz-sidebar-dragging');
    },
    move(e) {
      if (!drag.current) return;
      const dx = (e.clientX - drag.current.x) * (rtl !== end ? -1 : 1);
      sb.setWidth(drag.current.w + dx);
    },
    up(e, el) {
      drag.current = null;
      try { el.releasePointerCapture(e.pointerId); } catch { /* already released */ }
      document.documentElement.classList.remove('dz-sidebar-dragging');
    },
    key(e) {
      const step = e.shiftKey ? 64 : 16;
      const grow = rtl !== end ? 'ArrowLeft' : 'ArrowRight';
      const shrink = rtl !== end ? 'ArrowRight' : 'ArrowLeft';
      if (e.key === grow) sb.setWidth(sb.width + step);
      else if (e.key === shrink) sb.setWidth(sb.width - step);
      else if (e.key === 'Home') sb.setWidth(sb.min);
      else if (e.key === 'End') sb.setWidth(sb.max);
      else if (!end && (e.key === 'Enter' || e.key === ' ')) sb.setCollapsed(true);
      else return;
      e.preventDefault();
    },
    dbl() { if (end) sb.setWidth(sb.width === sb.min ? sb.max : sb.min); else sb.setCollapsed(true); },
  };
  return (
    <>
    <div role="separator" aria-orientation="vertical" aria-label={label} aria-valuenow={sb.width} aria-valuemin={sb.min} aria-valuemax={sb.max}
      tabIndex={0} className={`dz-sidebar__handle${end ? ' dz-sidebar__handle--end' : ''}${inline ? ' dz-sidebar__handle--inline' : ''}`}
      ref={handle} data-testid={`sidebar-handle-${sb.id}`} />
    {!end && <Tooltip content={t('ui.sidebar.hide')}>
      <button type="button" className="dz-sidebar__collapse" aria-label={t('ui.sidebar.hide')} onClick={sb.toggle} data-testid={`sidebar-hide-${sb.id}`}><PanelLeftClose aria-hidden size={16} /></button>
    </Tooltip>}
    </>
  );
}

export interface ResizableSidebarProps extends UseSidebarOptions {
  app: string; id: string;
  /** Accessible name of the sidebar. */
  label: string;
  as?: 'nav' | 'aside' | 'div';
  className?: string;
  children: ReactNode;
}

/**
 * A complete sidebar next to its content: a flex row you place in your layout, `<aside>` on the left and
 * the content in the right. Under 760 px it becomes an overlay drawer you open with `drawerOpen`.
 */
export function ResizableSidebar({ app, id, label, as: Tag = 'aside', className = '', children, defaultWidth, min, max, content, drawerOpen, onDrawerClose }: ResizableSidebarProps & {
  /** What sits beside the sidebar. */
  content?: ReactNode; drawerOpen?: boolean; onDrawerClose?(): void;
}) {
  const t = useT();
  const sb = useSidebar(app, id, { defaultWidth, min, max });
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!sb.narrow || !drawerOpen) return;
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>('a, button, [tabindex="0"]')?.focus();
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onDrawerClose?.(); };
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('keydown', esc); prev?.focus?.(); };
  }, [sb.narrow, drawerOpen, onDrawerClose]);
  const T = Tag as 'aside';
  return (
    <div className="dz-sidebar-host" {...sb.hostProps}>
      {sb.narrow && drawerOpen && <div className="dz-sidebar__scrim" onClick={onDrawerClose} aria-hidden />}
      <T ref={ref as never} className={`dz-sidebar ${sb.narrow && drawerOpen ? 'is-drawer' : ''} ${className}`} aria-label={label} data-testid={`sidebar-${id}`}>
        {children}
      </T>
      {content}
      <SidebarHandle sb={sb} label={t('ui.sidebar.resize_named', { label: label.toLowerCase() })} />
    </div>
  );
}

/**
 * Resize handle for a panel that is a flex or grid sibling on the inline-end side (Docs' comments, Sheets'
 * conditional formatting…). Put it just before the panel; the panel's width is `var(--dz-panel-w, 340px)`.
 * Remembers the width per app and panel kind, and sets `--dz-panel-w` on the page while mounted.
 */
export function PanelHandle({ app, id, label: named, defaultWidth = 340, min = 280, max = 640 }: { app: string; id: string; label?: string; defaultWidth?: number; min?: number; max?: number }) {
  const t = useT();
  const label = named ?? t('ui.sidebar.resize_panel');
  const sb = useSidebar(app, id, { defaultWidth, min, max, side: 'end', narrowQuery: '(max-width: 900px)' });
  useEffect(() => {
    const root = document.documentElement;
    if (sb.narrow) { root.style.removeProperty('--dz-panel-w'); return; }
    root.style.setProperty('--dz-panel-w', `${sb.width}px`);
    return () => { root.style.removeProperty('--dz-panel-w'); };
  }, [sb.width, sb.narrow]);
  return <span className="dz-panel-slot"><SidebarHandle sb={sb} label={label} inline /></span>;
}
